import { Card, CardContent, Dialog, DialogContent, Grid, Stack, Typography } from '@mui/material'
import { NEW } from 'common/components/action/ActionConstant'
import { CardTable } from 'common/components/datatable/Table'
import Input from 'common/components/form/Input'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import React, { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useActions from 'common/hooks/useActions'
import useBoolean from 'common/hooks/useBoolean'
import useTitles from 'common/hooks/useTitles'
import { hasPermission } from 'common/utils/HabilitationUtil'
import PropTypes from 'prop-types'
import { createProduct, deleteProduct, fetchProducts, updateProduct } from 'gdl/commercial/product/ProductsSlice'
import { toast } from 'react-toastify'
import ProductFeatureSelect2Checkbox from './ProductFeatureSelect2Checkbox'

const DialogProduct = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    selectedProduct = { features: [], id: 0, name: '' },
}) => {
    const {
        products,
    } = useSelector(store => ({
        products: store.gdl.products,
    }), shallowEqual)

    const [product, setProduct] = useState(selectedProduct)

    return (
        <Dialog
            open={isOpen}
            maxWidth={'lg'}
        >
            <BasicDialogTitle onClose={onClose}>
                Produit
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Card>
                            <CardContent>
                                <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                                    <Grid size={3}>
                                        <Input
                                            label='Nom'
                                            value={product.name}
                                            onChange={name => {
                                                setProduct(p => ({ ...p, name }))
                                                if (products.allProducts.some(p => p.name.toLowerCase() === name.toLowerCase())) {
                                                    toast.warning('Un Produit avec le même nom éxiste déjà')
                                                }
                                            }}
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={12}>
                        <ProductFeatureSelect2Checkbox
                            selectedFeatures={product.features}
                            setSelectedFeatures={tab => setProduct(p => ({ ...p, features: tab.map(v => ({ ...v, product: p.id })) }))
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={'Valider'}
                onValidate={() => onValidate(product)}
                isDisabled={!product?.name}
            />
        </Dialog>
    )
}

DialogProduct.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    product: PropTypes.string,
    selectedProduct: PropTypes.any,
}

const CommercialProductApp = () => {
    const dispatch = useDispatch()

    const {
        user,
        commercialFeatures,
        allProducts,
    } = useSelector(store => ({
        user: store.app.user,
        commercialFeatures: store.gdl.commercialFeatures,
        allProducts: store.gdl.products.allProducts,
    }), shallowEqual)

    useTitles(() => [{
        label: 'Commerce',
        href: '/commercial',
    }, {
        label: 'Produit',
        href: '/commercial/product',
    }], [])

    const [selectedProduct, setSelectedProduct] = useState()
    const {
        value: isDialogOpen,
        setTrue: onOpenDialog,
        setFalse: onCloseDialog,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)


    useActions(() => [{
        type: NEW,
        onClick: onOpenDialog,
        displayed: hasPermission(user, 'commercial', 'create'),
    }], [])

    const onUpdate = ({ product }) => {
        setSelectedProduct(product)
        onOpenDialog()
    }
    const onDelete = ({ product }) => {
        setSelectedProduct(product)
        onOpenConfirm()
    }

    const formattedProducts = allProducts.map(p => ({
        product: p,
        name: p.name,
        modules: p.features.map(f => commercialFeatures.byId[f.idFeature]?.name).filter(name => !!name).filter(str => !!str).join(', '),
    }))

    return (
        <>
            <Stack spacing={2} sx={{ padding: '0 15%' }}>
                <Card>
                    <CardContent>
                        <Stack spacing={1} sx={{ padding: 1 }}>
                            <Typography>Attention !</Typography>
                            <Typography>La modification des produits qui sont déjà activés sur les clients, entraine la modifications des licences de ces clients, et donc de leurs accès à l'application.</Typography>
                            <Typography>Règle de gestion 1 : la suppression d'un module d'un produit est supprimé aussi sur les produits affectés aux clients.</Typography>
                            <Typography>Règle de gestion 2 : L'ajout d'un module sur un produit le rajoute sur les produits affectés aux clients.</Typography>
                            <Typography>Règle de gestion 3 : La modification d'un module en Option pour le passer en "Full access" rajoute le module sur tous les produits affectés aux clients qui ne l'avaient pas encore.</Typography>
                            <Typography>Règle de gestion 4 : La suppression d'un produit supprime également ce produit de la liste des clients (et donc des accès qui vont avec).</Typography>
                        </Stack>
                    </CardContent>
                </Card>
                <CardTable
                    title='Produits'

                    headers={[{ key: 'name', value: 'Nom' }, { key: 'modules', value: 'Modules' }]}
                    rows={formattedProducts}
                    lineActions={[
                        {
                            icon: 'edit',
                            onClick: onUpdate,
                            displayed: hasPermission(user, 'commercial', 'update'),
                        },
                        {
                            icon: 'delete_outlined',
                            onClick: onDelete,
                            displayed: hasPermission(user, 'commercial', 'delete'),
                        },
                    ]}
                />
            </Stack>
            {
                isDialogOpen && (
                    <DialogProduct
                        isOpen={isDialogOpen}
                        onClose={() => {
                            setSelectedProduct()
                            onCloseDialog()
                        }}
                        onValidate={newProduct => {
                            dispatch((newProduct.id === 0 ? createProduct : updateProduct)(newProduct)).finally(() => {
                                dispatch(fetchProducts()).finally(() => {
                                    onCloseDialog()
                                    setSelectedProduct()
                                })
                            })
                        }}
                        selectedProduct={selectedProduct}
                    />
                )
            }
            <ConfirmModal
                isOpen={isConfirmOpen}
                title='Êtes-vous sûr de vouloir supprimer ce Produit ?'
                onValidate={() => {
                    dispatch(deleteProduct(selectedProduct)).then(() => {
                        dispatch(fetchProducts()).finally(() => {
                            setSelectedProduct()
                            onCloseConfirm()
                        })
                    })
                }}
                onClose={onCloseConfirm}
            />
        </>
    )
}

export default CommercialProductApp